import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useJoyrideContext } from "../../../contexts/JoyrideContext";
import { useUserContext } from "../../../contexts/UserContext";
import { TUTORIAL_TOURS, ProductTours } from "../../../config/tourConfig";
import { useContentfulContext } from "../../../contexts/ContentfulContext";
import { getAllExperiencesData, getOrgPlanMappings } from "../../../utils/APIs";
import Loader from "../../../components/UI/Loader/Loader";
import { permission } from "../../../config/permission";
import Card from "../../../components/UI/Card";
import Button from "../../../components/UI/Button";
import { Play, Compass } from "lucide-react";

const TutorialCard = ({ title, description, tourKey, icon }) => {
  const { setState, startTour } = useJoyrideContext();
  const { content } = useContentfulContext();
  const navigate = useNavigate();

  const handleStartTour = () => {
    const tourConfig = TUTORIAL_TOURS(content?.TOURS || {})[tourKey];
    if (tourConfig) {
      if (tourConfig.initialRoute) {
        navigate(tourConfig.initialRoute);
        setTimeout(() => {
          setState((prev) => ({ ...prev, steps: tourConfig.steps }));
          startTour(tourConfig.id);
        }, 500);
      } else {
        setState((prev) => ({ ...prev, steps: tourConfig.steps }));
        startTour(tourConfig.id);
      }
    }
  };

  return (
    <Card
      variant="accent"
      padding="none"
      className="group relative flex flex-col justify-between p-5 rounded-xl bg-gradient-to-b from-white via-white to-primary-soft/20 border border-border-subtle hover:border-primary/40 hover:shadow-card-hover hover:-translate-y-0.5 transition-all duration-200"
    >
      <div>
        <div className="flex items-start justify-between gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary-soft border border-primary/20 flex items-center justify-center p-2 text-primary group-hover:scale-105 transition-transform shrink-0 shadow-xs">
            {icon || <Compass className="w-5 h-5 text-current" />}
          </div>
          <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-semibold bg-primary-soft text-primary border border-primary/20">
            Interactive Tour
          </span>
        </div>

        <h3 className="text-base font-semibold text-stride-heading mt-4 mb-1.5 line-clamp-1 group-hover:text-primary transition-colors">
          {title}
        </h3>
        <p className="text-xs text-stride-muted leading-relaxed line-clamp-2 min-h-[32px]">
          {description}
        </p>
      </div>

      <div className="mt-5 pt-3 border-t border-border-subtle/80">
        <Button
          variant="secondary"
          size="sm"
          icon={<Play size={13} className="fill-current text-primary" />}
          onClick={handleStartTour}
          className="w-full justify-center"
        >
          Start Tour
        </Button>
      </div>
    </Card>
  );
};

const Tutorials = () => {
  const { userData, checkPermission } = useUserContext();
  const { content } = useContentfulContext();
  const [loading, setLoading] = useState(false);
  const [experiences, setExperiences] = useState([]);
  const [organizationPlan, setOrganizationPlan] = useState(null);

  const canBuyTopups = checkPermission(permission.SHOW_AI_GENERATE_VARIANTS) || checkPermission(permission.SHOW_AI_GENERATE_FIELDS);

  const getAllExperiencesHandler = async () => {
    try {
      setLoading(true);
      const result = await getAllExperiencesData(
        userData?.token,
        userData?.selectedOrganization?.org_token,
        userData?.defaultAdobeIO ? userData?.defaultAdobeIO._id : "",
        userData?.defaultContentful ? userData?.defaultContentful._id : "",
        userData?.defaultTealiumKey ? userData?.defaultTealiumKey?._id : "",
        userData?.defaultGithub ? userData?.defaultGithub?._id : "",
        userData?.defaultAEMAdobeIO ? userData?.defaultAEMAdobeIO?._id : ""
      );
      if (result.status) {
        setExperiences(result.data || []);
      }
    } catch (error) {
      console.error("Error fetching experiences:", error);
    } finally {
      setLoading(false);
    }
  };

  const getOrganizationPlanHandler = async () => {
    try {
      const result = await getOrgPlanMappings(userData?.token, userData?.selectedOrganization?.org_token);
      if (result.status && result.data?.length) {
        setOrganizationPlan(result.data[0]);
      }
    } catch (error) {
      console.error("Error fetching organization plan:", error);
    }
  };

  useEffect(() => {
    if (userData?.selectedOrganization) {
      getAllExperiencesHandler();
      getOrganizationPlanHandler();
    }
  }, [userData?.selectedOrganization]);

  const draftExperiences = experiences?.filter(exp => exp.status === 'draft');
  const publishedExperiences = experiences?.filter(exp => exp.status === 'published');

  return (
    <div className="stride-page-shell">
      {loading && <Loader />}
      <div className="w-full space-y-6">
        <div>
          <span className="stride-eyebrow">Interactive Guides</span>
          <h1 className="text-2xl font-bold tracking-tight text-stride-heading mt-0.5">
            {content?.TOURS?.title || "Product Tours & Tutorials"}
          </h1>
          <p className="text-sm text-stride-body mt-1">
            Step-by-step interactive walkthroughs to master Stride capabilities and workflows.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {ProductTours(content?.TOURS || {}).map((tutorial, index) => {
            const orgRole = userData?.selectedOrganization?.role?.[0]?.name;
            const orgLevel = userData?.selectedOrganization?.level?.id;

            if (tutorial?.showForOrgLevels && !tutorial.showForOrgLevels.includes(orgLevel)) {
              return null;
            }

            if (
              (tutorial.tourKey === 'EDIT_DRAFT_EXPERIENCE_TOUR' && !draftExperiences?.length) ||
              (tutorial.tourKey === 'EDIT_PUBLISHED_EXPERIENCE_TOUR' && !publishedExperiences?.length)
            ) {
              return null;
            }

            if (tutorial.tourKey === 'PURCHASE_TOPUP_TOUR' && (!canBuyTopups || organizationPlan?.plan_id?.is_custom_plan)) {
              return null;
            }

            if (tutorial.tourKey === 'UPGRADE_PLAN_TOUR' && organizationPlan?.plan_id?.is_custom_plan) {
              return null;
            }

            if (tutorial?.role?.length) {
              return tutorial.role.includes(orgRole) ? <TutorialCard key={index} {...tutorial} /> : null;
            }
            return <TutorialCard key={index} {...tutorial} />;
          })}
        </div>
      </div>
    </div>
  );
};

export default Tutorials;
